import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

export interface HighlightRange {
  start: number;
  end: number;
}

interface TrueImpactHighlightedTextOverlayProps {
  text: string;
  ranges: HighlightRange[];
  scrollTop?: number;
  style?: React.CSSProperties;
  className?: string;
}

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
  color: transparent;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  .highlight-match {
    color: transparent;
    border-radius: 2px;
    /* Translucent so the text rendered by the field underneath stays legible. */
    background-color: ${COLORS.criticalSubdued}59;
    box-shadow: 0 1px 0 0 ${COLORS.critical};
  }
`;

const toSegments = (text: string, ranges: HighlightRange[]) => {
  const segments: { value: string; highlighted: boolean }[] = [];
  let cursor = 0;
  ranges.forEach(({ start, end }) => {
    if (start > cursor) {
      segments.push({ value: text.slice(cursor, start), highlighted: false });
    }
    segments.push({ value: text.slice(start, end), highlighted: true });
    cursor = end;
  });
  if (cursor < text.length) {
    segments.push({ value: text.slice(cursor), highlighted: false });
  }
  return segments;
};

const TrueImpactHighlightedTextOverlay: React.FC<TrueImpactHighlightedTextOverlayProps> = ({
  text,
  ranges,
  scrollTop = 0,
  style,
  className,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const segments = useMemo(() => toSegments(text, ranges), [text, ranges]);

  useEffect(() => {
    if (overlayRef.current) overlayRef.current.scrollTop = scrollTop;
  }, [scrollTop, text]);

  if (!ranges.length) return null;
  return (
    <Overlay
      ref={overlayRef}
      className={className}
      style={style}
      aria-hidden="true"
    >
      {segments.map((segment, index) =>
        segment.highlighted ? (
          <span key={index} className="highlight-match">
            {segment.value}
          </span>
        ) : (
          <React.Fragment key={index}>{segment.value}</React.Fragment>
        ),
      )}
    </Overlay>
  );
};

export default TrueImpactHighlightedTextOverlay;
